/* global __PATH_PREFIX__ */
import * as Sentry from '@sentry/gatsby';

let initialized = false;
let controller = null;
let reloading = false;
let lastCheck = 0;
let checking = false;
let waitingWorker = null;
let updateAvailable = false;
let refreshRequested = false;
let updateCheck = 0;
let updateChannel = null;
const listeners = new Set();

const reloadOnce = () => {
	if (reloading) return;
	reloading = true;
	window.location.reload();
};

const handleUpdateMessage = (message) => {
	if (message?.type === 'REFRESH_REQUESTED') {
		refreshRequested = true;
	} else if (message?.type === 'RELOAD_NOW') {
		reloadOnce();
	}
};

const broadcastUpdateMessage = (type) => {
	const message = { type, id: `${Date.now()}-${Math.random()}` };
	if (updateChannel) {
		updateChannel.postMessage(message);
		return;
	}
	try {
		localStorage.setItem('finteq-service-worker-update-message', JSON.stringify(message));
	} catch (error) {
		Sentry.captureException(error, { tags: { operation: 'service-worker-update-broadcast' } });
	}
};

const notifyUpdate = async () => {
	const check = ++updateCheck;
	let available = true;
	try {
		const url = new URL('page-data/app-data.json', window.location.origin + __PATH_PREFIX__ + '/');
		url.searchParams.set('sw-version', `${Date.now()}-${check}`);
		const response = await fetch(url.href, { cache: 'no-store' });
		if (response.ok) {
			const { webpackCompilationHash } = await response.json();
			if (webpackCompilationHash && window.___webpackCompilationHash) {
				available = webpackCompilationHash !== window.___webpackCompilationHash;
			}
		}
	} catch (error) {
		return;
	}
	if (check !== updateCheck || updateAvailable === available) return;
	updateAvailable = available;
	listeners.forEach((listener) => listener(available));
};

export const subscribeServiceWorkerUpdate = (listener) => {
	listeners.add(listener);
	listener(updateAvailable);
	return () => listeners.delete(listener);
};

export const serviceWorkerUpdateReady = (registration) => {
	if (!registration?.waiting) return;
	waitingWorker = registration.waiting;
	notifyUpdate();
};

export const refreshServiceWorker = () => {
	if (reloading || refreshRequested) return;
	if (waitingWorker && waitingWorker.state !== 'activated' && waitingWorker.state !== 'redundant') {
		refreshRequested = true;
		broadcastUpdateMessage('REFRESH_REQUESTED');
		waitingWorker.postMessage({ type: 'SKIP_WAITING' });
	} else {
		broadcastUpdateMessage('RELOAD_NOW');
		reloadOnce();
	}
};

export const checkServiceWorkerUpdate = async () => {
	if (!initialized || checking || navigator.onLine === false || Date.now() - lastCheck < 30000) return;
	checking = true;
	lastCheck = Date.now();
	try {
		const registration = await navigator.serviceWorker.getRegistration();
		if (registration) {
			serviceWorkerUpdateReady(registration);
			await registration.update();
		}
	} catch (error) {
		Sentry.captureException(error, { tags: { operation: 'service-worker-update' } });
	} finally {
		checking = false;
	}
};

export const initializeServiceWorkerUpdates = () => {
	if (initialized || process.env.NODE_ENV !== 'production' || !('serviceWorker' in navigator)) return;
	initialized = true;
	if ('BroadcastChannel' in window) {
		updateChannel = new BroadcastChannel('finteq-service-worker-updates');
		updateChannel.addEventListener('message', (event) => handleUpdateMessage(event.data));
	} else {
		window.addEventListener('storage', (event) => {
			if (event.key !== 'finteq-service-worker-update-message' || !event.newValue) return;
			try {
				handleUpdateMessage(JSON.parse(event.newValue));
			} catch (error) {
				Sentry.captureException(error, { tags: { operation: 'service-worker-update-message-parse' } });
			}
		});
	}
	controller = navigator.serviceWorker.controller;
	navigator.serviceWorker.addEventListener('controllerchange', () => {
		const nextController = navigator.serviceWorker.controller;
		if (!nextController || nextController === controller) return;
		const isUpdate = controller !== null;
		controller = nextController;
		if (refreshRequested && !reloading) {
			reloadOnce();
		} else if (isUpdate) {
			waitingWorker = null;
			notifyUpdate();
		}
	});
	document.addEventListener('visibilitychange', () => {
		if (document.visibilityState === 'visible') checkServiceWorkerUpdate();
	});
	window.addEventListener('online', checkServiceWorkerUpdate);
	checkServiceWorkerUpdate();
};
